import React, { useState } from 'react';
import { Button } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ButtonLoadingIcon from 'components/atoms/icons/buttonLoadingIcon';
import { differenceInCalendarDays } from 'date-fns';
import { capitalize } from 'lodash';
import { useAccess } from 'infrastructure/hooks/access/useAccess';
import HoldingStatusModal from 'components/molecules/holding-status-modal';
import EmailsResubscriptionModal from 'components/molecules/resubscribeToEmails/modal';
import CcmInteractionStatusModal from 'components/molecules/ccmInteractionStatus/modal';
import {
  useUpdatePatientHoldingStatus,
  useUpdateTempHoldStatus,
  useUpdateConsumablesDate,
  useEmailSubscriptionStatus,
  useUpdateInteractionStatus,
} from 'infrastructure/hooks/control-panel';
import { parseDateStringWithoutTimeZoneAdopt } from 'infrastructure/functions';
import useKey from 'infrastructure/hooks/utils/use-key';
import forms from 'constants/forms';
import useUser from 'utils/useUser';
import useMonthlyInteractionAlert from 'infrastructure/hooks/utils/use-monthly-interaction-alert';

import s from './styles.module.scss';

import type { FC } from 'react';
import type { ModuleType } from 'infrastructure/enums';

interface StatusItemProps {
  patientGuid: string;
  module: ModuleType;
  emailSubscription?: boolean;
  ccmHoldingStatus?: boolean;
}

export const StatusButtons: FC<StatusItemProps> = ({
  patientGuid,
  module,
  emailSubscription,
  ccmHoldingStatus,
}) => {
  const { t } = useTranslation();

  const { hasClinicalRole, isBiller } = useUser();

  const {
    patientInfo,
    isCombinedModule,
    isCcmModule,
    isReadOnlyRole,
    isBlockCcmModule,
    isCcmActionDisabled,
    isBlockRpmModule,
    isRpmModule,
  } = useAccess({
    module,
    patientGuid,
  });

  const [isFollowUpModalOpen, setIsFollowUpModalOpen] = useState(false);
  const [isHoldingStatusModalOpen, setIsHoldingStatusModalOpen] =
    useState(false);
  const [isEmailSubscriptionModalOpen, setIsEmailSubscriptionModalOpen] =
    useState(false);

  const { key, changeKey } = useKey();

  const { updateHoldingStatus, holdingStatusLoading } =
    useUpdatePatientHoldingStatus({
      onSuccess() {
        setIsHoldingStatusModalOpen(false);
        changeKey();
      },
    });
  const { updateTempHoldStatus, tempHoldingStatusLoading } =
    useUpdateTempHoldStatus({
      onSuccess() {
        setIsHoldingStatusModalOpen(false);
        changeKey();
      },
    });
  const { updateConsumablesDate, consumablesDateLoading } =
    useUpdateConsumablesDate({
      onSuccess() {
        setIsHoldingStatusModalOpen(false);
        changeKey();
      },
    });

  const { updateSubscriptionStatus, subscriptionStatusLoading } =
    useEmailSubscriptionStatus({
      patientGuid,
      onSuccess() {
        setIsEmailSubscriptionModalOpen(false);
      },
    });

  const { MonthlyInteractionAlert, showMonthlyInteractionAlert } =
    useMonthlyInteractionAlert({
      patientGuid,
    });

  const { updateInteractionStatus, updateInteractionStatusLoading } =
    useUpdateInteractionStatus();

  const handleMonthlyInteractionClick = async () => {
    const res = await showMonthlyInteractionAlert({
      ignoreIsShowed: true,
    });
    if (res) {
      updateInteractionStatus({
        patientGuid,
        isInteracted: !patientInfo?.interactionStatus,
      });
    }
  };

  const monthlyInteractionRpmDisabled =
    (isCombinedModule && (isReadOnlyRole || isBlockRpmModule)) ||
    patientInfo?.interactionStatus;

  const holdingStatusLoadingState =
    holdingStatusLoading || tempHoldingStatusLoading || consumablesDateLoading;

  const holdingStatusStartTime = parseDateStringWithoutTimeZoneAdopt(
    patientInfo?.holdingStatus?.startTime,
  );
  const holdingStatusEndTime = parseDateStringWithoutTimeZoneAdopt(
    patientInfo?.holdingStatus?.endTime,
  );

  const isAppointmentFollowUpComplete = !!patientInfo?.ccmInteractionStatus;
  const isFollowUpDisabled =
    (isCombinedModule && (isReadOnlyRole || isBlockCcmModule)) ||
    !!patientInfo?.ccmInteractionStatus ||
    isCcmActionDisabled;
  const isHoldingStatus = ccmHoldingStatus ?? false;
  const isEmailSubscribed = emailSubscription ?? false;

  const remainingDays =
    holdingStatusEndTime &&
    differenceInCalendarDays(holdingStatusEndTime, new Date());

  const currentHoldingStatus =
    patientInfo?.holdingStatus?.status !== 'active' &&
    typeof patientInfo?.holdingStatus?.endTime !== 'undefined' &&
    typeof remainingDays !== 'undefined'
      ? `${remainingDays + 1} Day${remainingDays > 1 ? 's' : ''} ${capitalize(
          patientInfo?.holdingStatus.status,
        )}`
      : undefined;

  return (
    <>
      {(isCombinedModule || isRpmModule) && hasClinicalRole && (
        <div
          className={s['status-item']}
          data-cy="control-panel-item-monthly-interaction"
        >
          <div className={s['status-content']}>
            <span className={s['status-label']}>
              {t('labels.monthlyInteractionRpm')}
            </span>
            <span
              className={
                patientInfo?.interactionStatus
                  ? s['status-value']
                  : s['status-value-false']
              }
            >
              {patientInfo?.interactionStatus
                ? t('labels.yes')
                : t('labels.no')}
            </span>
          </div>
          <Button
            className={s['status-edit-button']}
            icon={
              updateInteractionStatusLoading ? (
                <ButtonLoadingIcon className={s['spin-loading-icon']} />
              ) : (
                <EditOutlined />
              )
            }
            onClick={handleMonthlyInteractionClick}
            data-cy="control-panel-button-monthly-interaction"
            disabled={
              updateInteractionStatusLoading || monthlyInteractionRpmDisabled
            }
          />
        </div>
      )}
      {(isCcmModule || isCombinedModule) && (
        <div
          className={s['status-item']}
          data-cy="control-panel-item-follow-up-appointment"
        >
          <div className={s['status-content']}>
            <span className={s['status-label']}>
              {t('labels.appointmentFollowUpComplete')}
            </span>
            <span
              className={
                isAppointmentFollowUpComplete
                  ? s['status-value']
                  : s['status-value-false']
              }
            >
              {isAppointmentFollowUpComplete
                ? t('labels.true')
                : t('labels.false')}
            </span>
          </div>
          <Button
            className={s['status-edit-button']}
            icon={<EditOutlined />}
            onClick={() => setIsFollowUpModalOpen(true)}
            disabled={isFollowUpDisabled}
            data-cy="control-panel-button-follow-up-appointment"
          />
        </div>
      )}

      {!isBiller && (
        <div
          className={s['status-item']}
          data-cy="control-panel-item-holding-status"
        >
          <div className={s['status-content']}>
            <span className={s['status-label']}>
              {t('labels.holdingStatus')}
            </span>
            <span className={s['status-value']}>
              {currentHoldingStatus ??
                (isHoldingStatus ? t('labels.yes') : t('labels.no'))}
            </span>
          </div>
          <Button
            className={s['status-edit-button']}
            icon={<EditOutlined />}
            onClick={() => setIsHoldingStatusModalOpen(true)}
            data-cy="control-panel-button-holding-status"
            disabled={isCombinedModule ? isReadOnlyRole : isCcmActionDisabled}
          />
        </div>
      )}

      <div
        className={s['status-item']}
        data-cy="control-panel-item-email-subscription"
      >
        <div className={s['status-content']}>
          <span className={s['status-label']}>
            {t('labels.emailSubscription')}
          </span>
          <span className={s['status-value']}>
            {isEmailSubscribed
              ? t('labels.subscribed')
              : t('labels.unsubscribed')}
          </span>
        </div>
        <Button
          className={s['status-edit-button']}
          icon={<EditOutlined />}
          onClick={() => setIsEmailSubscriptionModalOpen(true)}
          disabled={
            isEmailSubscribed ||
            (isCombinedModule && isReadOnlyRole) ||
            isCcmActionDisabled
          }
          data-cy="control-panel-button-email-subscription"
        />
      </div>

      <CcmInteractionStatusModal
        isOpen={isFollowUpModalOpen}
        toggle={setIsFollowUpModalOpen}
        patientGuid={patientGuid}
      />

      <HoldingStatusModal
        key={key}
        isOpen={isHoldingStatusModalOpen}
        patientGuid={patientGuid}
        toggle={setIsHoldingStatusModalOpen}
        patientHoldingStatus={patientInfo?.holdingStatus?.status ?? null}
        nextHoldingStatus={patientInfo?.holdingStatus?.nextStatus ?? null}
        holdingStatusStartTime={holdingStatusStartTime}
        holdingStatusEndTime={holdingStatusEndTime}
        updateHoldingStatus={updateHoldingStatus}
        options={forms.patientHoldingStatuses}
        loading={holdingStatusLoadingState}
        module={module}
        ccmHoldingStatus={patientInfo?.ccmHoldingStatus}
        ccmHoldingStatusStartDate={patientInfo?.ccmHoldingStatusStartDate}
        updateTempHoldStatus={updateTempHoldStatus}
        tempHoldingStatusLoading={tempHoldingStatusLoading}
        consumablesArrivingDate={patientInfo?.consumablesArrivingDate}
        updateConsumablesDate={updateConsumablesDate}
        isReadOnly={false}
        consumablesDateLoading={consumablesDateLoading}
      />

      <EmailsResubscriptionModal
        setIsOpen={setIsEmailSubscriptionModalOpen}
        isOpen={isEmailSubscriptionModalOpen}
        loading={subscriptionStatusLoading}
        onSubmit={async (d) => updateSubscriptionStatus(d)}
      />
      {MonthlyInteractionAlert}
    </>
  );
};
